import { useLocation } from 'wouter';
import { useGetMe, useLogout, getGetMeQueryKey } from '@workspace/api-client-react';
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Menu, ArrowLeft } from 'lucide-react';
import { navigationHistory } from '@/services/NavigationHistoryService';
import { Sidebar } from './Sidebar';
import { OperatorAvatar } from '@/components/OperatorAvatar';
import { OperatorCenterMenu } from '@/components/OperatorCenterMenu';
import OperatorCreationWizard from '@/components/OperatorCreationWizard';
import RoamerControlScreen from '@/components/auth/RoamerControlScreen';
import WelcomeBackScreen from '@/components/WelcomeBackScreen';
import { recordLastRoute, consumeResumeTarget, hydrationOrchestrator } from '@/services/HydrationOrchestrator';
import { bootLogger } from '@/lib/bootLogger';
import { shouldOfferResume, storeResumeOffer } from '@/services/resumeOffer';
import { installBackGuard, setBackGuardNavigator } from '@/services/backGuard';
import { loadPersona, updateLastLogin, markFirstLoginComplete, clearPersona, clearLocalPersona } from '@/services/OperatorPersonaService';
import {
  publishSignedOut,
  publishSessionRestored,
  publishSessionExpired,
} from '@/services/AuthEventService';
import { supabase } from '@/lib/supabaseClient';
import { setCurrentUserId } from '@/services/ErrorTelemetry';
import { needsReconsent, recordTermsAcceptance, CURRENT_TERMS_VERSION, CURRENT_PRIVACY_VERSION, TERMS_EFFECTIVE_DATE } from '@/lib/termsVersion';
import BetaFeedbackWidget from '@/components/platform/BetaFeedbackWidget';

// ---------------------------------------------------------------------------
// DEV BYPASS — only when VITE_ENFORCE_SECURITY is not "true" AND in DEV mode.
// Allows the dev server to start without API credentials. In production or
// when VITE_ENFORCE_SECURITY=true, this bypass is never active.
// ---------------------------------------------------------------------------
const SECURITY_ENFORCED = import.meta.env.VITE_ENFORCE_SECURITY === 'true';
const DEV_AUTH_BYPASS = import.meta.env.DEV && !SECURITY_ENFORCED;
const DEV_MOCK_USER = DEV_AUTH_BYPASS
  ? { id: 0, email: 'dev@nomad.local', displayName: 'Dev Operator', createdAt: new Date().toISOString() }
  : null;

// ---------------------------------------------------------------------------
// Opening scan sequence — shown once per browser session after login.
// ---------------------------------------------------------------------------
const SCAN_STEPS = [
  'Scanning remote opportunities…',
  'Checking saved companies…',
  'Ranking opportunities…',
  'Preparing today\'s recommendations…',
  'Finding your highest earning opportunity…',
];

const INTRO_KEY = 'wr-intro-seen';

function hasSeenIntro(): boolean {
  try { return !!sessionStorage.getItem(INTRO_KEY); } catch { return true; }
}
function markIntroSeen(): void {
  try { sessionStorage.setItem(INTRO_KEY, '1'); } catch { /* ignore */ }
}
/** WELCOME HOME™ (2.08.0): show the cloud-restore screen once per browser session. */
function welcomeSeen(): boolean {
  try { return !!sessionStorage.getItem('wr-welcome-seen'); } catch { return true; }
}

function IntroScreen({ onDone }: { onDone: () => void }) {
  const [step, setStep]     = useState(0);
  const [fading, setFading] = useState(false);
  const doneCalled          = useRef(false);

  useEffect(() => {
    const interval = 280;
    const totalMs  = SCAN_STEPS.length * interval + 300;

    const stepTimer = setInterval(() => {
      setStep(s => Math.min(s + 1, SCAN_STEPS.length - 1));
    }, interval);

    const fadeTimer = setTimeout(() => setFading(true), totalMs - 350);

    const doneTimer = setTimeout(() => {
      if (!doneCalled.current) { doneCalled.current = true; onDone(); }
    }, totalMs);

    return () => {
      clearInterval(stepTimer);
      clearTimeout(fadeTimer);
      clearTimeout(doneTimer);
    };
  }, [onDone]);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        background: 'radial-gradient(ellipse 80% 70% at 50% 35%, #0b2060 0%, #04112b 55%, #010a1a 100%)',
        transition: 'opacity 0.35s ease', opacity: fading ? 0 : 1,
        pointerEvents: fading ? 'none' : 'auto',
        fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
      }}
    >
      <img
        src="/workroamer-brand.png"
        alt="WorkRoamer™ — Where work finds you"
        style={{ width: 240, height: 'auto', display: 'block', marginBottom: 32, userSelect: 'none' }}
      />
      <div style={{ minHeight: 20, textAlign: 'center' }}>
        {SCAN_STEPS.map((msg, i) => (
          <div
            key={msg}
            style={{
              display: i === step ? 'block' : 'none',
              color: 'rgba(255,255,255,0.55)', fontFamily: 'monospace', fontSize: 11, letterSpacing: '0.06em',
            }}
          >
            {msg}
          </div>
        ))}
      </div>
      <div style={{ marginTop: 20, width: 180, height: 2, background: 'rgba(59,130,246,0.15)', borderRadius: 1, overflow: 'hidden' }}>
        <div style={{
          height: '100%', width: `${((step + 1) / SCAN_STEPS.length) * 100}%`,
          background: 'linear-gradient(90deg, #2563eb, #3b82f6)', transition: 'width 0.28s ease', borderRadius: 1,
        }} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shell
// ---------------------------------------------------------------------------
export function Shell({ children }: { children: React.ReactNode }) {
  // OPERATOR IDENTITY™ (2.06.0): useGetMe is ALWAYS enabled.
  // DEV_AUTH_BYPASS allows the dev server to run without credentials when
  // VITE_ENFORCE_SECURITY is not set — in all other cases auth is enforced.
  const { data: apiUser, isLoading, isFetching } = useGetMe({
    query: { queryKey: getGetMeQueryKey(), retry: false, enabled: true },
  });
  const [location, setLocation] = useLocation();
  const queryClient     = useQueryClient();
  const logout          = useLogout();
  const [drawerOpen,    setDrawerOpen]    = useState(false);
  const [showIntro,     setShowIntro]     = useState(() => !hasSeenIntro());
  const [menuOpen,      setMenuOpen]      = useState(false);
  const [onboarding,    setOnboarding]    = useState(false);
  const [welcome,       setWelcome]       = useState(false);
  const [roamerControl, setRoamerControl] = useState(false);
  const [reconsentOpen, setReconsentOpen] = useState(false);
  const [verifyBannerDismissed, setVerifyBannerDismissed] = useState(() => {
    try { return sessionStorage.getItem('wr-email-verify-dismissed') === '1'; } catch { return false; }
  });
  const [resendingVerify,  setResendingVerify]  = useState(false);
  const [resendVerifyNote, setResendVerifyNote] = useState<string | null>(null);

  // Session event tracking — fire once per mount only.
  const sessionEventFiredRef = useRef(false);

  // Publish auth bus events when session check resolves + stamp lastLogin.
  useEffect(() => {
    if (isLoading || sessionEventFiredRef.current) return;
    sessionEventFiredRef.current = true;
    if (apiUser) {
      const _u = apiUser as unknown as Record<string, unknown>;
      console.log(
        `[NOMAD_DEBUG] Shell /auth/me resolved: ` +
        `internal_id=${_u['id']} ` +
        `email=${_u['email']} ` +
        `displayName=${_u['displayName'] ?? 'null'} ` +
        `role=${_u['role'] ?? 'n/a'} ` +
        `supabaseId=${_u['supabaseId'] ?? 'n/a'}`,
      );
      publishSessionRestored(apiUser);
      updateLastLogin();
      // Stamp the authenticated user id in ErrorTelemetry so that any
      // subsequent AppErrorBoundary catch can include it in the report.
      setCurrentUserId(apiUser.id);
      // Re-consent gate: show modal once if stored terms version differs from
      // the current version (CURRENT_TERMS_VERSION / CURRENT_PRIVACY_VERSION).
      // New users are handled by the signup flow — needsReconsent() returns
      // false when no acceptance record exists.
      if (needsReconsent()) setReconsentOpen(true);
    } else {
      publishSessionExpired();
      setCurrentUserId(null);
    }
  }, [isLoading, apiUser]);

  // ── PHASE 4: User-ID isolation guard ──────────────────────────────────────
  //
  // Fires once when apiUser first resolves after login. Compares the incoming
  // user ID against `nomad:last-user-id` stored from the previous session. If
  // the IDs differ (different user on same browser, or post-account-switch), we
  // wipe all user-scoped localStorage keys so no data from User A bleeds into
  // User B's session.
  //
  // This is the safety net for cases where wipeClientState() was bypassed
  // (browser closed without logout, app crash, etc.).
  //
  // WHY run here and not in wipeClientState(): wipeClientState() runs on logout.
  // But if User A logs out → localStorage cleared → User B signs up → app crash
  // before new login → User A's key is still in localStorage from a different
  // browser session. The guard catches every such edge case at login time.
  const userIsolationFired = useRef(false);
  useEffect(() => {
    if (!apiUser || userIsolationFired.current) return;
    userIsolationFired.current = true;
    try {
      const currentId = String(apiUser.id);
      const storedId  = localStorage.getItem('nomad:last-user-id');

      if (storedId !== null && storedId !== currentId) {
        // Different user detected — wipe all user-scoped keys before this user's
        // services read from localStorage. This prevents location, persona, and
        // session data from a previous user appearing in the new session.
        const CROSS_USER_KEYS = [
          'nomad:operator:persona:v1',
          'nomad:location:v1',
          'nomad:location-refresh:v1',
          'nomad:sessions:v1',
          'nomad:mc-state',
          'nomad:operator_history',
          'nomad:roamer-profile:v1',
          'nomad:cloud-sync-meta:v1',
          'nomad:cloud-sync-queue:v1',
          'nomad_activation_completed_v1',
          'nomad:session-continuity:v1',
          'nomad:first-boot-running',
        ];
        for (const key of CROSS_USER_KEYS) {
          try { localStorage.removeItem(key); } catch { /* ignore */ }
        }
        // Clear query cache — it may contain data fetched for the previous user.
        queryClient.clear();
        console.log(
          `[NOMAD_DEBUG] User-ID isolation: wiped stale cross-user cache ` +
          `(prev=${storedId} → curr=${currentId})`,
        );
      }

      // Always stamp current user ID so the next login can detect a switch.
      localStorage.setItem('nomad:last-user-id', currentId);
    } catch {
      // localStorage unavailable — skip silently.
    }
  }, [apiUser, queryClient]);

  // Determine effective user: real API user first; fall back to DEV mock only
  // when the auth bypass is active (DEV mode + VITE_ENFORCE_SECURITY not set).
  const user = apiUser ?? (DEV_AUTH_BYPASS ? DEV_MOCK_USER : null);

  // ── SMART NAVIGATION™ — global history stack + return experience ────────
  const mainRef = useRef<HTMLElement | null>(null);
  const prevRouteRef = useRef<string | null>(null);
  const [canGoBack, setCanGoBack] = useState(() => navigationHistory.canGoBack());

  useEffect(() => {
    const el = mainRef.current;
    const prev = prevRouteRef.current;
    if (el && prev != null && prev !== location) {
      navigationHistory.saveScroll(prev, el.scrollTop);
    }
    const action = navigationHistory.recordNavigation(location);
    prevRouteRef.current = location;
    setCanGoBack(navigationHistory.canGoBack());
    if (el) {
      const target = action === 'pop' ? navigationHistory.currentScrollTop() : 0;
      requestAnimationFrame(() => {
        el.scrollTop = target;
        if (action === 'pop' && target > 0) navigationHistory.markScrollRestored(location);
      });
    }
  }, [location]);

  // External-return experience.
  useEffect(() => {
    const restore = () => {
      const pending = navigationHistory.pendingReturn();
      if (pending == null) return;
      navigationHistory.consumeReturn();
      const el = mainRef.current;
      if (el != null && pending.route === navigationHistory.currentRoute()) {
        requestAnimationFrame(() => {
          el.scrollTop = pending.scrollTop;
          navigationHistory.markScrollRestored(pending.route);
        });
      }
    };
    const onVisible = () => { if (document.visibilityState === 'visible') restore(); };
    // BUG FREEZE™ Stage 2 (2.15.0) — ANDROID/BROWSER BACK GUARD.
    // Module-level singleton: Shell mounts per protected route, so the guard
    // (sentinel entry + popstate listener + markBackIntent) must be installed
    // exactly once. Each mount only refreshes the navigator.
    installBackGuard();
    setBackGuardNavigator(setLocation);
    document.addEventListener('visibilitychange', onVisible);
    navigationHistory.registerScrollTopGetter(() => mainRef.current?.scrollTop ?? 0);
    restore();
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      navigationHistory.registerScrollTopGetter(null);
    };
  }, []);

  // Universal Back — priority: history stack → browser history → Home.
  const handleBack = () => {
    const prev = navigationHistory.back();
    if (prev != null) setLocation(prev);
    else if (window.history.length > 1) window.history.back();
    else setLocation('/home');
  };
  // Always show the back button on any page that isn't Home.
  // Previously gated on canGoBack, but that hides the button when the user
  // deep-links or restores a tab — they have no way to navigate back at all.
  // handleBack already has the right fallback chain:
  //   internal NavigationHistory stack → window.history.back() → /home
  const showBack = location !== '/home';

  // OPERATOR IDENTITY™ (2.06.0): route protection.
  // Redirect to /login when the session is definitively missing or expired.
  // RC-HOTFIX: Gate on !isFetching — do NOT redirect while a background
  // refetch is in flight. After login/signup, invalidateQueries triggers a
  // refetch; isFetching=true + isLoading=false + data=undefined is the
  // "background refetch for fresh data" state, not an "unauthenticated" state.
  // Redirecting during this window causes the stale-cache bounce loop.
  useEffect(() => {
    if (isLoading || isFetching) return;
    if (!apiUser && !DEV_AUTH_BYPASS) {
      setLocation('/login');
    }
  }, [isLoading, isFetching, apiUser, setLocation]);

  // Show onboarding if persona not yet set up; returning operators get the
  // WELCOME HOME™ cloud-restore screen instead (once per browser session).
  // OPERATOR IDENTITY™ Phase 3: when no completed local persona exists, wait
  // for the cloud profile hydration step first — a returning operator on a
  // fresh device must land in Welcome Home, not re-onboarding.
  //
  // RC-01 (Build 2.21.0): WelcomeBackScreen only intercepts when the operator
  // is navigating to /home. Deep links, bookmarks, and shared workspace URLs
  // (e.g. /wallet, /passport, /companies) must NEVER be intercepted — the
  // operator should land directly at their intended destination.
  const decideBootExperience = useRef((
    setters: { onboarding: (v: boolean) => void; welcome: (v: boolean) => void },
    pathname: string,
    serverOnboarded?: boolean,
  ) => {
    // Only show the welcome screen on the home route. Any direct workspace
    // navigation bypasses it so bookmarks, deep links and notifications work.
    try {
      const isHomeRoute = pathname === '/home' || pathname === '/';

      // ── Auth Stabilization Phase 1: server-authoritative fast path ───────
      // When GET /auth/me returns onboardingCompleted=true the server is the
      // definitive source of truth for returning users. No localStorage read
      // or cloud hydration required — eliminates the fresh-browser wizard
      // regression for accounts that have completed onboarding on any device.
      if (serverOnboarded === true) {
        console.log('[NOMAD_DEBUG] decideBootExperience → SERVER SAYS ONBOARDED (returning user fast path)');
        // Server fast-path returns before localStorage or cloud reads — mark those skipped
        // so all 11 canonical steps are resolved and useBootSequence().isComplete can be true.
        bootLogger.record('persona-load',     'skip', 'server-fast-path — no localStorage read needed');
        bootLogger.record('cloud-hydration',  'skip', 'server-fast-path — no cloud read needed');
        bootLogger.record('onboarding-check', 'ok', 'server:true → returning-user fast path');
        bootLogger.record('boot-decision', 'ok',
          welcomeSeen() || !isHomeRoute ? 'NORMAL' : 'WELCOME');
        // Returning users: pipeline does not run — opportunities already in DB.
        bootLogger.record('opportunities',    'skip', 'returning user — data already in DB');
        bootLogger.record('mission-pipeline', 'skip', 'returning user — no first-boot pipeline');
        bootLogger.record('dashboard-ready',  'ok',   'returning user fast path complete');
        if (!welcomeSeen() && isHomeRoute) {
          try { sessionStorage.setItem('wr-welcome-seen', '1'); } catch { /* ignore */ }
          console.log('[NOMAD_DEBUG] decideBootExperience → WELCOME (server-confirmed returning user)');
          setters.welcome(true);
        } else {
          console.log('[NOMAD_DEBUG] decideBootExperience → NORMAL (server-confirmed onboarded, welcome seen or deep link)');
        }
        // RC1 FIX (Build 2.54.0): Bootstrap.ts fires runFullHydration() before the
        // Bearer token is registered → all /api/persistence calls get 401 → profile
        // step ends as 'no-data'. This fast-path previously returned here without ever
        // calling waitForProfileHydration(), so the force-rerun path inside it never
        // triggered. Cloud data (location, wallet, passport, etc.) was never restored
        // for returning users on a fresh browser.
        // Fire-and-forget — never blocks the boot decision; waitForProfileHydration()
        // internally detects 'no-data'/'failed' and force-reruns with the valid token.
        void hydrationOrchestrator.waitForProfileHydration();
        return;
      }

      // ── Fresh-signup flag — consume but do NOT short-circuit ───────────────
      // ONBOARDING LOOP FIX: the flag is consumed here to prevent it being
      // re-read on a future re-render, but we NO LONGER use it as an early
      // return that bypasses localStorage and cloud hydration.
      //
      // WHY: the flag was being set conservatively by AuthCallbackPage on any
      // network error (even for returning users), and the early-return skipped
      // both the localStorage persona check AND cloud hydration. Result: a
      // returning user whose /auth/me fetch failed got sent to the wizard even
      // though their cloud persona had onboardingCompleted:true.
      //
      // The flag is now only used downstream as a hint that skips the welcome
      // screen (new users should not see "Welcome Back") — it no longer triggers
      // an immediate wizard launch on its own.
      let isFreshSignup = false;
      try {
        isFreshSignup = !!sessionStorage.getItem('nomad:fresh-signup');
        if (isFreshSignup) sessionStorage.removeItem('nomad:fresh-signup');
      } catch { /* sessionStorage unavailable — fall through */ }

      console.log(
        `[NOMAD_DEBUG] decideBootExperience START: ` +
        `pathname=${pathname} isHomeRoute=${isHomeRoute} isFreshSignup=${isFreshSignup}`,
      );

      // ── Synchronous persona check (localStorage) ────────────────────────
      let persona: ReturnType<typeof loadPersona> = null;
      try { persona = loadPersona(); } catch { /* fall through to onboarding */ }

      console.log(
        `[NOMAD_DEBUG] decideBootExperience PERSONA (localStorage): ` +
        `found=${persona !== null} ` +
        `onboardingCompleted=${persona?.onboardingCompleted ?? 'null'} ` +
        `displayName=${persona?.displayName ?? 'null'} ` +
        `currentBase=${persona?.currentBase?.city ?? 'null'} ` +
        `homeBase=${persona?.homeBase?.city ?? 'null'} ` +
        `country=${persona?.country ?? 'null'} ` +
        `createdAt=${persona?.createdAt ?? 'null'}`,
      );

      if (persona?.onboardingCompleted) {
        const ws = welcomeSeen();
        console.log(
          `[NOMAD_DEBUG] decideBootExperience → persona complete. ` +
          `welcomeSeen=${ws} isHomeRoute=${isHomeRoute}`,
        );
        bootLogger.record('persona-load', 'ok',
          `onboardingCompleted=true city=${persona.currentBase?.city ?? 'null'}`);
        // localStorage path returns before cloud hydration — mark it skip so all
        // 11 canonical steps resolve and useBootSequence().isComplete can be true.
        bootLogger.record('cloud-hydration',  'skip', 'localStorage persona path — cloud read skipped');
        bootLogger.record('onboarding-check', 'ok', 'localStorage persona complete');
        bootLogger.record('boot-decision', 'ok', ws || !isHomeRoute ? 'NORMAL' : 'WELCOME');
        // Returning users: pipeline does not run — opportunities already in DB.
        bootLogger.record('opportunities',    'skip', 'returning user — data already in DB');
        bootLogger.record('mission-pipeline', 'skip', 'returning user — no first-boot pipeline');
        bootLogger.record('dashboard-ready',  'ok',   'localStorage persona path complete');
        if (!ws && isHomeRoute) {
          try { sessionStorage.setItem('wr-welcome-seen', '1'); } catch { /* ignore */ }
          console.log('[NOMAD_DEBUG] decideBootExperience → WELCOME (returning, first load this session)');
          setters.welcome(true);
        } else {
          console.log('[NOMAD_DEBUG] decideBootExperience → NORMAL (persona complete, welcome already seen or not home route)');
        }
        // RC1 FIX (Build 2.54.0): localStorage persona path previously skipped cloud
        // hydration entirely. This meant module data (wallet, passport, visa) and the
        // post-hydration location reconciliation (reconcileCurrentBase) never ran for
        // same-browser returning users. Cloud location never overrode the stale
        // localStorage value (e.g. Pai, Thailand stored with source='saved').
        // Fire-and-forget — waitForProfileHydration() handles the no-data → force-rerun
        // path internally; never blocks or changes the boot decision already made above.
        void hydrationOrchestrator.waitForProfileHydration();
        return;
      }

      // ── Async cloud hydration ────────────────────────────────────────────
      console.log('[NOMAD_DEBUG] decideBootExperience: no complete local persona — waiting for cloud hydration');
      bootLogger.record('persona-load', persona ? 'skip' : 'skip', 'no completed persona, awaiting cloud');
      void hydrationOrchestrator.waitForProfileHydration()
        .then(() => {
          try {
            const hydrated = loadPersona();
            console.log(
              `[NOMAD_DEBUG] decideBootExperience POST-HYDRATION: ` +
              `found=${hydrated !== null} ` +
              `onboardingCompleted=${hydrated?.onboardingCompleted ?? 'null'} ` +
              `displayName=${hydrated?.displayName ?? 'null'} ` +
              `currentBase=${hydrated?.currentBase?.city ?? 'null'} ` +
              `country=${hydrated?.country ?? 'null'}`,
            );
            bootLogger.record('cloud-hydration', 'ok',
              `onboardingCompleted=${hydrated?.onboardingCompleted ?? 'null'}`);
            if (hydrated?.onboardingCompleted) {
              bootLogger.record('onboarding-check', 'ok', 'cloud persona complete');
              bootLogger.record('boot-decision', 'ok',
                welcomeSeen() || !isHomeRoute ? 'NORMAL' : 'WELCOME');
              // Returning users: pipeline does not run — opportunities already in DB.
              bootLogger.record('opportunities',    'skip', 'returning user — data already in DB');
              bootLogger.record('mission-pipeline', 'skip', 'returning user — no first-boot pipeline');
              bootLogger.record('dashboard-ready',  'ok',   'cloud-hydration persona path complete');
              if (!welcomeSeen() && isHomeRoute) {
                try { sessionStorage.setItem('wr-welcome-seen', '1'); } catch { /* ignore */ }
                console.log('[NOMAD_DEBUG] decideBootExperience → WELCOME (cloud-restored persona)');
                setters.welcome(true);
              } else {
                console.log('[NOMAD_DEBUG] decideBootExperience → NORMAL (cloud persona, welcome seen or deep link)');
              }
            } else {
              console.log(
                `[NOMAD_DEBUG] decideBootExperience → WIZARD (no completed persona after hydration). ` +
                `hydrated=${JSON.stringify(hydrated ?? null)}`,
              );
              bootLogger.record('onboarding-check', 'skip', 'no persona after cloud hydration → wizard');
              bootLogger.record('boot-decision', 'ok', 'WIZARD (cloud-no-persona)');
              try { sessionStorage.setItem('nomad:wizard-active', '1'); } catch { /* ignore */ }
              setters.onboarding(true);
            }
          } catch (e) {
            console.log('[NOMAD_DEBUG] decideBootExperience → WIZARD (loadPersona threw inside .then):', e);
            bootLogger.record('cloud-hydration', 'fail', String(e));
            bootLogger.record('boot-decision', 'ok', 'WIZARD (hydration threw)');
            setters.onboarding(true);
          }
        })
        .catch((e) => {
          console.log('[NOMAD_DEBUG] decideBootExperience → WIZARD (waitForProfileHydration rejected):', e);
          bootLogger.record('cloud-hydration', 'fail', String(e));
          bootLogger.record('boot-decision', 'ok', 'WIZARD (hydration rejected)');
          setters.onboarding(true);
        });
    } catch (e) {
      console.log('[NOMAD_DEBUG] decideBootExperience → WIZARD (outer block threw):', e);
      setters.onboarding(true);
    }
  }).current;

  // RC-HOTFIX: boot experience is only safe to run after auth is confirmed.
  // Gating on (!isLoading && !isFetching && effectiveUser) prevents the wizard
  // from being set while the auth state is still uncertain.
  // showIntro in deps: for users that see the intro, auth resolves while the
  // intro is playing; when the intro calls setShowIntro(false), this effect
  // re-runs and fires decideBootExperience — no direct call needed in onDone.
  const bootExperienceFired = useRef(false);
  const effectiveUserForBoot = apiUser ?? (DEV_AUTH_BYPASS ? DEV_MOCK_USER : null);
  useEffect(() => {
    // Still loading auth, or background-refetching after login (isFetching),
    // or not authenticated, or already fired — do nothing.
    if (isLoading || (isFetching && !apiUser) || !effectiveUserForBoot) return;
    if (bootExperienceFired.current) return;
    if (showIntro) return; // intro in progress — fire when it ends (showIntro→false)
    bootExperienceFired.current = true;
    try {
      const serverOnboarded = (apiUser as unknown as { onboardingCompleted?: boolean })?.onboardingCompleted;
      // ── [BOOT] — structured decision-point log (Task #38) ──────────────────
      let _localPersonaSnap: ReturnType<typeof loadPersona> = null;
      try { _localPersonaSnap = loadPersona(); } catch { /* ignore */ }
      const _freshSignup = (() => { try { return !!sessionStorage.getItem('nomad:fresh-signup'); } catch { return false; } })();
      // Record internal user resolution on first boot decision.
      bootLogger.record('internal-user', apiUser ? 'ok' : 'fail',
        apiUser ? `id=${apiUser.id} email=${apiUser.email}` : 'apiUser null at boot');
      console.log(
        '[BOOT] step=boot-decision status=pending ms=n/a\n' +
        `  serverOnboarded       : ${serverOnboarded ?? 'undefined'}\n` +
        `  localPersonaExists    : ${_localPersonaSnap !== null}\n` +
        `  localOnboarded        : ${_localPersonaSnap?.onboardingCompleted ?? 'null'}\n` +
        `  localCurrentBase      : ${_localPersonaSnap?.currentBase?.city ?? 'null'}\n` +
        `  freshSignupFlag       : ${_freshSignup}\n` +
        `  wizardActive          : ${(() => { try { return !!sessionStorage.getItem('nomad:wizard-active'); } catch { return 'unavailable'; } })()}\n` +
        `  welcomeSeen           : ${(() => { try { return !!sessionStorage.getItem('wr-welcome-seen'); } catch { return 'unavailable'; } })()}\n` +
        `  apiUser.id            : ${apiUser?.id ?? 'null'}\n` +
        `  pathname              : ${location}`,
      );
      decideBootExperience({ onboarding: setOnboarding, welcome: setWelcome }, location, serverOnboarded);
    } catch {
      // decideBootExperience has its own try/catch, but if something escapes,
      // fall back to onboarding — never leave the user on a blank screen.
      setOnboarding(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isFetching, apiUser, showIntro]);

  function handleIntroDone() {
    markIntroSeen();
    setShowIntro(false);
    // NOTE: do NOT call decideBootExperience here. Setting showIntro=false
    // causes the effect above to re-run (showIntro is a dep). The effect fires
    // decideBootExperience after verifying auth is confirmed. Calling it here
    // too would cause a double-invoke race.
  }

  // OPERATOR ACTIVATION™ (2.09.0): the operator lands on Home immediately
  // after activation — First Home Experience. Stamp firstLoginComplete.
  // RC-3 (2.22.2): dispatch 'nomad:onboarding-complete' so useFirstBoot's
  // onboarding gate re-evaluates and the first-boot pipeline can now fire.
  function handleActivationComplete() {
    setOnboarding(false);
    setRoamerControl(true);
    markFirstLoginComplete();
    // Build 2.34.0: clear the wizard-active sentinel before dispatching the
    // completion event so useFirstBoot sees a clean state when it re-evaluates.
    try { sessionStorage.removeItem('nomad:wizard-active'); } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent('nomad:onboarding-complete'));
    // Auth Stabilization Phase 1: persist to server so the DB is the
    // authoritative source of truth for returning-user detection. Fire-and-
    // forget — the localStorage persona + cloud sync are the backup. Bearer
    // token is injected automatically by bearerFetchPatch.ts.
    // Boot logging — Task #38
    bootLogger.record('onboarding-check', 'ok', 'wizard complete → writing to server');
    // ── BOOT_PROBE: Q2 — Is completion written to the server DB? ──────────────
    // RC2 FIX (Build 2.54.0): POST previously had no retry. A transient network
    // failure (or brief CSRF token absence in Brave/Firefox on first load) would
    // silently leave onboardingCompleted=false in the DB. On a fresh browser with
    // no local persona and no cloud persona (sync queue not yet flushed), the next
    // login would show the wizard again. Fix: retry up to 2 additional times with
    // a 1.5 s delay so transient failures do not corrupt the server's boot authority.
    const _postOnboardingComplete = async (attemptsRemaining: number): Promise<void> => {
      console.log(
        `[BOOT] step=onboarding-write status=pending ms=n/a ` +
        `detail=POST /api/auth/complete-onboarding (attempts_remaining=${attemptsRemaining})`,
      );
      try {
        // RC-FIX (Onboarding Loop): inject Bearer token on every attempt.
        // The bare fetch without Authorization was returning 401 for Supabase
        // users (no cookie session), silently leaving onboardingCompleted=false
        // in the DB. On the next login GET /auth/me returned false → server
        // fast-path never fired → returning user was sent to the wizard.
        // Pattern mirrors startActivation()'s income-goal PATCH.
        let bearerToken: string | undefined;
        try {
          const { data: sessionData } = await supabase.auth.getSession();
          bearerToken = sessionData.session?.access_token;
        } catch { /* non-fatal — fall back to cookie-only */ }

        const res = await fetch('/api/auth/complete-onboarding', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
          },
        });
        const body = await res.json().catch(() => ({}));
        console.log(
          `[BOOT] step=onboarding-write status=${res.ok ? 'ok' : 'fail'} ms=n/a ` +
          `detail=status=${res.status} body=${JSON.stringify(body)}`,
        );
        if (!res.ok && attemptsRemaining > 0) {
          console.warn(`[BOOT] onboarding-write: status=${res.status} — retrying in 1.5 s`);
          await new Promise(r => setTimeout(r, 1500));
          return _postOnboardingComplete(attemptsRemaining - 1);
        }
        bootLogger.record('onboarding-check', res.ok ? 'ok' : 'fail',
          `server status=${res.status} ok=${res.ok} attempts_used=${3 - attemptsRemaining}`);
      } catch (err) {
        console.warn('[BOOT] onboarding-write: network error —', String(err));
        if (attemptsRemaining > 0) {
          await new Promise(r => setTimeout(r, 1500));
          return _postOnboardingComplete(attemptsRemaining - 1);
        }
        bootLogger.record('onboarding-check', 'fail',
          `network error after 3 attempts: ${String(err)}`);
      }
    };
    void _postOnboardingComplete(2);
  }

  function handleWelcomeDone() {
    try { sessionStorage.setItem('wr-welcome-seen', '1'); } catch { /* ignore */ }
    setWelcome(false);
    // BUG FREEZE™ Stage 2 (2.15.0): after authentication the operator ALWAYS
    // lands on Home first. Continue Where You Left Off™ no longer auto-opens
    // the previous workspace; a non-Home resume target becomes an explicit
    // Resume banner on Home the operator can accept or dismiss.
    const resume = consumeResumeTarget();
    if (resume && shouldOfferResume(resume.route)) {
      storeResumeOffer(resume.route, resume.scrollY);
    }
  }

  // SESSION CONTINUITY™ (2.08.0): record every route so the operator can
  // resume exactly where they left off — on this device or any other.
  // Phase 5 (2.10.0): also capture the main-pane scroll offset (debounced)
  // so Continue Where You Left Off™ can restore the exact position.
  useEffect(() => {
    recordLastRoute(location, mainRef.current?.scrollTop ?? 0);
    const el = mainRef.current;
    if (!el) return;
    let t: ReturnType<typeof setTimeout> | null = null;
    const onScroll = () => {
      if (t) clearTimeout(t);
      t = setTimeout(() => recordLastRoute(location, el.scrollTop), 600);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (t) clearTimeout(t);
    };
  }, [location]);

  // Loading screen while auth check is in flight — or while a background
  // refetch is resolving with no user data yet (stale-cache-after-login case).
  // RC-HOTFIX: (isFetching && !apiUser) covers the race where invalidateQueries
  // was called post-login/signup, triggering a background refetch. Without this
  // check, Shell would see isLoading=false+data=undefined and redirect to /login.
  // BLANK-SCREEN FIX (Build 2.29.0): bg-background is added so the loading div
  // never shows a transparent white body through it. The CONNECTING text is
  // visible on both light and dark themes via text-primary.
  if ((isLoading || (isFetching && !apiUser)) && !DEV_AUTH_BYPASS) {
    return (
      <div className="h-dvh-shell w-full bg-background flex items-center justify-center text-primary font-mono text-sm animate-pulse tracking-widest">
        CONNECTING…
      </div>
    );
  }

  // Not authenticated and no bypass — redirect effect above handles navigation.
  // BLANK-SCREEN FIX (Build 2.29.0): return a themed placeholder instead of
  // null so the brief window between auth resolving and setLocation('/login')
  // firing shows a solid background rather than a transparent blank canvas.
  if (!user) return <div className="h-dvh-shell w-full bg-background" />;

  const handleLogout = () => {
    setDrawerOpen(false);
    const operatorId = user.id;

    // Shared client-side cleanup — runs whether the server call succeeds or fails.
    // MUST run before redirect so a new account never inherits a previous user's state.
    const wipeClientState = () => {
      queryClient.clear();
      // clearLocalPersona() wipes localStorage only — does NOT queue a cloud DELETE.
      // Using clearPersona() here (which calls storageManager.remove()) would queue
      // a cloud DELETE that fires on the user's next login and permanently deletes
      // their persona from Supabase, causing the onboarding wizard to re-show on
      // every subsequent login. Cloud persistence is the operator's authoritative
      // record; it must survive logout so hydration can restore it on the next session.
      clearLocalPersona();
      // PHASE 3 — Location clear on logout (RC-5):
      // nomad:location:v1 was never cleared on logout. After logout, a different
      // user logging in on the same browser would inherit the previous user's
      // location (e.g. Tucson/Phoenix). Clear it here alongside the persona so
      // the next login always derives location fresh from the new user's profile.
      const USER_SCOPED_KEYS = [
        'nomad_activation_completed_v1',
        'nomad:roamer-profile:v1',
        'nomad:session-continuity:v1',
        'nomad:first-boot-running',
        'nomad:cloud-sync-meta:v1',
        // Location keys — must be cleared so the next user starts with their own location
        'nomad:location:v1',
        'nomad:location-refresh:v1',
        // Operator-specific caches
        'nomad:sessions:v1',
        'nomad:mc-state',
        'nomad:operator_history',
      ];
      for (const key of USER_SCOPED_KEYS) {
        try { localStorage.removeItem(key); } catch { /* ignore */ }
      }
      try { sessionStorage.removeItem(INTRO_KEY); } catch { /* ignore */ }
      try { sessionStorage.removeItem('wr-welcome-seen'); } catch { /* ignore */ }
      try { sessionStorage.removeItem('nomad:wizard-active'); } catch { /* ignore */ }
      try { sessionStorage.removeItem('nomad:first-boot-launched'); } catch { /* ignore */ }
    };

    // Sign out of Supabase Auth (clears the JWT session from localStorage).
    // This is fire-and-forget; even if it fails the cookie session is cleared below.
    supabase.auth.signOut().catch(() => { /* ignore — cookie session will expire */ });

    logout.mutate(undefined, {
      onSuccess: () => {
        publishSignedOut(operatorId);
        wipeClientState();
        setLocation('/login');
      },
      onError: () => {
        // Server-side session deletion failed (network error, unexpected 500, etc.).
        // Best-effort: wipe all client state anyway and redirect. The server session
        // will expire naturally; the auth guard will block re-entry without a cookie.
        wipeClientState();
        setLocation('/login');
      },
    });
  };

  // Display name: prefer displayName, fall back to email prefix.
  const displayName = (user as { displayName?: string | null }).displayName
    || user.email.split('@')[0];

  return (
    <>
      {showIntro && <IntroScreen onDone={handleIntroDone} />}

      <div className="flex h-dvh-shell bg-background text-foreground font-sans overflow-hidden">

        {/* ── Mobile top bar (hidden on md+) ─────────────────────────────── */}
        <header className="md:hidden fixed top-0 left-0 right-0 z-30 h-12 flex items-center justify-between px-4 border-b border-border bg-card shrink-0">
          <span className="flex items-center">
            {showBack && (
              <button
                onClick={handleBack}
                className="w-10 h-10 flex items-center justify-center text-muted-foreground hover:text-foreground"
                aria-label="Back"
                data-testid="button-nav-back"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={() => setDrawerOpen(true)}
              className="w-10 h-10 flex items-center justify-center text-muted-foreground hover:text-foreground"
              aria-label="Open navigation"
            >
              <Menu className="w-5 h-5" />
            </button>
          </span>

          <img
            src="/workroamer-brand.png"
            alt="WorkRoamer™ — Where work finds you"
            style={{ height: 34, width: 'auto', display: 'block', objectFit: 'contain', flexShrink: 0 }}
          />

          <OperatorAvatar size="sm" showBadge onClick={() => setLocation('/profile')} />
        </header>

        {/* ── Sidebar ──────────────────────────────────────────────────────── */}
        <Sidebar
          userEmail={displayName}
          onLogout={handleLogout}
          isLoggingOut={logout.isPending}
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
        />

        {/* ── Main content ─────────────────────────────────────────────────── */}
        <main ref={mainRef} className="flex-1 overflow-y-auto overflow-x-hidden relative bg-background">
          <div className="hidden md:block absolute top-0 left-0 w-full h-px bg-gradient-to-r from-primary/20 via-primary/40 to-primary/20" />

          {/* Email Verification Reminder — now shown as a floating notification
               card below (Task 71). Removed from inside <main> to avoid
               layout disruption during onboarding and welcome screens.      */}

          <div className="px-4 pb-6 pt-[3.75rem] md:p-10 max-w-7xl mx-auto">
            {showBack && (
              <button
                onClick={handleBack}
                className="hidden md:inline-flex items-center gap-1.5 mb-4 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Back"
                data-testid="button-nav-back-desktop"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Back
              </button>
            )}
            {children}
          </div>
        </main>
      </div>

      <OperatorCenterMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
      {onboarding && <OperatorCreationWizard onComplete={handleActivationComplete} />}
      {roamerControl && (
        <RoamerControlScreen
          persona={(() => { try { return loadPersona(); } catch { return null; } })()}
          onComplete={() => setRoamerControl(false)}
        />
      )}
      {welcome && !onboarding && <WelcomeBackScreen onDone={handleWelcomeDone} />}

      {/* ── Beta Feedback Widget (Sprint 097) — visible to all authed users ── */}
      {apiUser && <BetaFeedbackWidget />}

      {/* ── Email Verification Reminder™ (Task 71) ──────────────────────────
           Friendly floating notification. Appears only AFTER the user has
           fully completed onboarding and is on the live dashboard — never
           during the wizard, welcome screen, or intro animation.
           Dismissed per browser session via sessionStorage.              */}
      {!onboarding && !welcome && (() => {
        const emailVerified = (user as { emailVerified?: boolean }).emailVerified ?? true;
        const role = (user as { role?: string }).role ?? 'USER';
        if (emailVerified || role === 'OWNER' || verifyBannerDismissed) return null;

        const handleResend = async () => {
          if (resendingVerify) return;
          setResendingVerify(true);
          setResendVerifyNote(null);
          try {
            const res = await fetch('/api/auth/resend-verification', {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: (user as { email: string }).email }),
            });
            const data = await res.json() as { message?: string };
            setResendVerifyNote(data.message ?? 'Verification email sent. Check your inbox.');
          } catch {
            setResendVerifyNote('Network error — please try again.');
          } finally {
            setResendingVerify(false);
          }
        };

        const handleDismiss = () => {
          try { sessionStorage.setItem('wr-email-verify-dismissed', '1'); } catch { /* ignore */ }
          setVerifyBannerDismissed(true);
        };

        return (
          <div
            role="alert"
            aria-live="polite"
            style={{
              position: 'fixed', bottom: 20, right: 20, zIndex: 9000,
              width: 300, borderRadius: 14,
              background: 'hsl(222,14%,11%)',
              border: '1px solid rgba(59,130,246,0.28)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.55), 0 0 0 1px rgba(59,130,246,0.08)',
              padding: '16px 18px',
              fontFamily: "'Inter','Segoe UI',system-ui,sans-serif",
              animation: 'wr-notify-slide-in 0.3s cubic-bezier(0.16,1,0.3,1)',
            }}
          >
            <style>{`
              @keyframes wr-notify-slide-in {
                from { opacity: 0; transform: translateY(16px); }
                to   { opacity: 1; transform: translateY(0); }
              }
            `}</style>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                  background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.22)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                  </svg>
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0' }}>Protect Your Account</span>
              </div>
              <button
                onClick={handleDismiss}
                aria-label="Dismiss email verification reminder"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#475569', fontSize: 18, lineHeight: 1, padding: '2px', flexShrink: 0, fontFamily: 'inherit' }}
              >
                ×
              </button>
            </div>

            {/* Body */}
            {resendVerifyNote ? (
              <p style={{ fontSize: 12, color: '#22c55e', lineHeight: 1.55, margin: '0 0 0' }}>
                ✓ {resendVerifyNote}
              </p>
            ) : (
              <>
                <p style={{ fontSize: 12, color: '#64748b', lineHeight: 1.65, margin: '0 0 14px' }}>
                  Verify your email to improve account security and enable full account recovery.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <button
                    onClick={handleResend}
                    disabled={resendingVerify}
                    style={{
                      width: '100%', padding: '9px 14px', borderRadius: 8,
                      background: resendingVerify
                        ? 'rgba(59,130,246,0.35)'
                        : 'linear-gradient(90deg, #1d4ed8, #3b82f6)',
                      border: 'none', cursor: resendingVerify ? 'not-allowed' : 'pointer',
                      color: '#fff', fontSize: 12, fontWeight: 700,
                      letterSpacing: '0.04em', fontFamily: 'inherit',
                      opacity: resendingVerify ? 0.65 : 1,
                      transition: 'opacity 0.2s',
                    }}
                  >
                    {resendingVerify ? 'Sending…' : 'Verify Email'}
                  </button>
                  <button
                    onClick={handleDismiss}
                    style={{
                      width: '100%', padding: '7px 14px', borderRadius: 8,
                      background: 'transparent',
                      border: '1px solid rgba(255,255,255,0.08)',
                      cursor: 'pointer', color: '#475569', fontSize: 12,
                      fontFamily: 'inherit', fontWeight: 600,
                    }}
                  >
                    Remind Me Later
                  </button>
                </div>
              </>
            )}
          </div>
        );
      })()}

      {/* ── Re-consent modal (Sprint 095) ────────────────────────────────── */}
      {reconsentOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="reconsent-title"
          style={{
            position: 'fixed', inset: 0, zIndex: 99999,
            background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '1rem',
          }}
        >
          <div style={{ background: 'hsl(222,14%,13%)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '2rem', maxWidth: 480, width: '100%' }}>
            <div style={{ fontSize: 11, fontFamily: 'monospace', color: '#6b7280', marginBottom: 8, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Updated Terms · Effective {TERMS_EFFECTIVE_DATE}
            </div>
            <h2 id="reconsent-title" style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 700, color: '#f9fafb' }}>
              We've updated our Terms & Privacy Policy
            </h2>
            <p style={{ margin: '0 0 10px', fontSize: 13, color: '#9ca3af', lineHeight: 1.7 }}>
              Our Terms of Service (v{CURRENT_TERMS_VERSION}) and Privacy Policy (v{CURRENT_PRIVACY_VERSION}) have been updated.
              Please review and accept to continue using WorkRoamer™.
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
              {[['Terms of Service', '/terms'], ['Privacy Policy', '/privacy']].map(([label, href]) => (
                <a
                  key={href}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 12, color: '#10b981', textDecoration: 'underline' }}
                >
                  {label} ↗
                </a>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10, flexDirection: 'column' }}>
              <button
                onClick={() => { recordTermsAcceptance(); setReconsentOpen(false); }}
                style={{
                  padding: '12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: '#10b981', color: '#fff', fontSize: 14, fontWeight: 700,
                }}
              >
                I Accept the Updated Terms
              </button>
              <button
                onClick={() => { /* Logout — user declined new terms */ handleLogout(); }}
                style={{
                  padding: '10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.12)',
                  cursor: 'pointer', background: 'transparent', color: '#6b7280', fontSize: 12,
                }}
              >
                Decline &amp; Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
